import * as routerActions from 'connected-react-router'
import { parse, stringify } from 'querystringify'
import * as Sentry from '@sentry/browser'
import Cookie from 'js-cookie'

// Actions
import { actions as collectionsActions } from 'modules/collections/actions'
import { actions as featureToggleActions } from 'modules/featuretoggles/actions'
import { actions as notificationActions } from 'modules/notifications/actions'
import { actions as priceActions } from 'modules/price/actions'
import { actions as profileActions } from 'modules/profile/actions'
import { actions as userSongActions } from 'modules/usersongs/actions'

// Types
import { Dispatch, GetState, APIAction, SimpleAction, Profile, APIActionDataResponse, SocialInfo } from 'types'

// Utils
import { safeLocalStorage as localStorage } from 'utils'
import { isBrowser } from '@process'
import { TRACKING_ENABLED, APP_SERVICE_GTAG } from '@constants'
import jsTracking from 'lib/jsTracking'
import loadFacebookSDK from 'lib/loadfbsdk'

const saveAuthToken = (token: string) => {
  if (isBrowser) {
    Cookie.set('savedAuthToken', token)
  }
}

const removeAuthToken = () => {
  if (isBrowser) {
    Cookie.remove('savedAuthToken')
  }
}

const setTrackingData = ({ id, email, alias, groups }: { id: number; email: string; alias: string; groups: Array<string> }) => {
  ga('set', {
    dimension1: id.toString(),
    dimension2: id !== 0 && id !== undefined,
    id,
    anonymizeIp: true,
  })

  gtag('config', `${APP_SERVICE_GTAG}`, {
    custom_map: { dimension1: 'User id', dimension2: 'Logged in' },
    user_id: id,
  })
  gtag('set', {
    'User id': id.toString(),
    'Logged in': id !== 0 && id !== undefined,
  })

  Sentry.configureScope(scope => {
    scope.setUser({ email, id: String(id), username: alias, groups })
  })
}

if (isBrowser) {
  if (window && window.location && window.location.search && window.location.search.indexOf('?impersonate=') > -1) {
    const userKey = window.location.search.split('?impersonate=')[1]
    if (userKey.length === 40) {
      localStorage.setItem('isImpersonating', 'true')
      saveAuthToken(userKey)
    }
  }
}

export const getAuthToken = (): string => (isBrowser ? Cookie.get('savedAuthToken') || '' : '')

export const actions = {
  toggleAdminMode: () => (dispatch: Dispatch) => dispatch({ type: 'TOGGLE_ADMIN_MODE' }),
  showAuthModal: (newLoc: string) => ({
    type: 'AUTH_SHOW_LOGIN_MODAL',
    data: newLoc,
  }),

  connectAndFetchSocial: () => (dispatch: Dispatch) =>
    new Promise((accept, reject) => {
      dispatch({ type: 'FETCH_FACEBOOK_SDK_START' })
      loadFacebookSDK().then(FB => {
        dispatch({ type: 'FETCH_FACEBOOK_SDK_DONE' })
        FB.login(
          r => {
            if (r.authResponse) {
              FB.api('/me', { fields: ['name', 'email', 'picture'] }, (profile: Profile) => {
                accept({
                  accessToken: r.authResponse.accessToken,
                  profile,
                })
              })
            } else {
              reject(r)
            }
          },
          { scope: 'email' },
        )
      })
    }).then(data => {
      dispatch({ type: 'AUTH_SET_PREVIEW', data: data as SocialInfo })
      return dispatch(routerActions.push('?modal=socialconfirm'))
    }),

  connectSocial: () => (dispatch: Dispatch) =>
    new Promise((accept, reject) => {
      dispatch({ type: 'FETCH_FACEBOOK_SDK_START' })
      loadFacebookSDK().then(FB => {
        dispatch({ type: 'FETCH_FACEBOOK_SDK_DONE' })
        FB.login(
          r => {
            if (r.authResponse) {
              FB.api('/me', { fields: ['name', 'email', 'picture'] }, profile => {
                accept({
                  accessToken: r.authResponse.accessToken,
                  profile,
                })
              })
            } else {
              reject(r)
            }
          },
          { scope: 'email' },
        )
      })
    }),

  registerAccount: (data: Record<string, unknown>, provider: string): APIAction => ({
    CALL_API: {
      type: 'AUTH_REGISTER',
      method: 'POST',
      endpoint: provider ? `/api/auth/register/${provider}/` : '/api/registration/',
      data,
    },
  }),

  registerAndLogin: (data: Record<string, unknown>, provider: string) => (dispatch: Dispatch, getState: GetState) => {
    const state = getState()
    const search = state.router.location?.search
    const { redirect } = parse(search) as { redirect: string }
    return dispatch<{ type: 'AUTH_REGISTER_ERROR'; data: { response: { key: string } } }>(actions.registerAccount(data, provider)).then(
      responseAction => {
        if (responseAction.type === 'AUTH_REGISTER_ERROR') {
          return responseAction
        }
        saveAuthToken(responseAction.data.response.key)
        return dispatch<APIActionDataResponse>(profileActions.fetchProfile()).then(action => {
          const profile = action.data.response.entities.profiles[action.data.response.result[0]]
          dispatch(actions.postAuthSuccess(profile, true))
          jsTracking('CompleteRegistration')

          if (redirect) {
            dispatch(routerActions.replace(decodeURIComponent(redirect)))
          }
          return Promise.resolve(responseAction)
        })
      },
    )
  },

  loginAndRedirect: (loginData: Record<string, unknown>, provider?: string) => async (dispatch: Dispatch, getState: GetState) => {
    localStorage.removeItem('isImpersonating')
    const state = getState()
    const { search, pathname } = state.router.location
    const { redirect, modal, ...searchRest } = parse(search) as { redirect: string; modal: string } // eslint-disable-line

    let loginPromise
    if (provider === 'facebook') {
      const { accessToken } = (await dispatch(actions.connectSocial())) as { accessToken: string }
      loginPromise = (await dispatch(actions.fbLogin({ access_token: accessToken }))) as {
        data: { error?: string; response: { key: string } }
      }
    } else {
      loginPromise = (await dispatch(actions.login(loginData))) as { data: { error?: string; response: { key: string } } }
    }
    const { data } = loginPromise
    if (data.error) {
      return Promise.resolve({ data })
    }
    const {
      response: { key },
    } = data

    await dispatch(actions.authCheck(key))

    if (redirect) {
      return dispatch(routerActions.replace(decodeURIComponent(redirect)))
    }
    return dispatch(routerActions.replace(`${pathname}?${stringify(searchRest)}`))
  },

  removeAuthToken: () => (dispatch: Dispatch) => {
    removeAuthToken()
    return dispatch({
      type: 'AUTH_REMOVE_TOKEN',
    })
  },

  authCheck: (savedToken?: string) => async (dispatch: Dispatch) => {
    dispatch({ type: 'AUTH_CHECK_START' })
    const token: string | null | undefined = savedToken || getAuthToken()
    if (!token) {
      dispatch(actions.removeAuthToken())
      dispatch({ type: 'AUTH_CHECK_DONE' })
    } else {
      saveAuthToken(token)
      const {
        data: {
          response: {
            result,
            entities: { profiles },
          },
        },
      } = await dispatch<APIActionDataResponse>(profileActions.fetchProfile())
      const userProfile = profiles[result[0]]
      if (TRACKING_ENABLED) {
        setTrackingData(userProfile)
      }
      await dispatch(actions.postAuthSuccess(userProfile))
      return dispatch(actions.postAuth(userProfile))
    }
    return dispatch(actions.postAuth())
  },

  // eslint-disable-next-line consistent-return
  postAuth: (profile?: Profile) => (dispatch: Dispatch, getState: GetState) => {
    const state = getState()
    const {
      location: { pathname, search },
    } = state.router
    const currentQuery = parse(search)
    const isRootPage = pathname === '/'
    const isSubscribePage = pathname.startsWith('/subscribe')
    const isBlogPage = pathname.startsWith('/blog')
    const isOnboarding = pathname.startsWith('/onboarding')
    let subscription
    if (profile) {
      if (typeof profile.subscription === 'number') {
        subscription = state.entities.subscriptions[String(profile.subscription)]
      } else {
        subscription = profile.subscription
      }
    }
    if (profile && !profile.onboarded && !isSubscribePage) {
      return dispatch(routerActions.push('/onboard'))
    }
    const { modal } = parse(search) as { modal: string }
    const someModalOpen = !!modal
    const lastDismissedDate = new Date(localStorage.getItem('starttrialmodaldismiss') || '')
    lastDismissedDate.setDate(lastDismissedDate.getDate() + 7)
    const dismissedRecently = lastDismissedDate > new Date()
    if (
      !dismissedRecently &&
      !isRootPage &&
      !isSubscribePage &&
      !isBlogPage &&
      !isOnboarding &&
      !someModalOpen &&
      subscription?.state === 'FREE'
    ) {
      setTimeout(() => {
        const newSearch = stringify({
          ...currentQuery,
          modal: 'starttrial',
        })
        dispatch(routerActions.push(`?${newSearch}`))
      }, 1000)
      localStorage.setItem('starttrialmodaldismiss', new Date().toString())
    } else {
      const shouldOpenTermsModal =
        profile &&
        profile.accepted_eula === 0 &&
        !pathname.startsWith('/terms') &&
        !pathname.startsWith('/cookies') &&
        !pathname.startsWith('/privacy')
      if (shouldOpenTermsModal) {
        dispatch(routerActions.push('?modal=terms'))
      }
    }
  },

  postAuthSuccess:
    (profile: Profile, isRegistration = false) =>
    (dispatch: Dispatch) =>
      Promise.all([
        dispatch(actions.authCheckSuccess(profile.id)),
        ...(!isRegistration ? [dispatch(notificationActions.fetchLocalReminders())] : []),
        dispatch(userSongActions.fetchRevenueReports(undefined, true)),
        dispatch(collectionsActions.fetchOwnCollections()),
        dispatch(priceActions.fetchUserDiscounts()),
        dispatch(featureToggleActions.fetchFeatureToggles()),
      ]).then(() => {
        dispatch(notificationActions.createLocalRevenueReportingReminder())
      }),

  authCheckSuccess: (profileId: number): SimpleAction => ({
    type: 'AUTH_CHECK_SUCCESS',
    data: profileId,
  }),

  exitSubscribePage: (redirect?: string) => (dispatch: Dispatch, getState: GetState) => {
    const { profileId } = getState().auth
    const { onboarded } = getState().entities.profiles[String(profileId)]
    if (!onboarded) {
      const query = redirect ? { redirect } : {}
      return dispatch(
        routerActions.push({
          pathname: '/onboard',
          search: stringify(query),
        }),
      )
    }
    return dispatch(routerActions.push(redirect || '/inspiration/'))
  },

  onboardingCompleted: () => (dispatch: Dispatch, getState: GetState) => {
    const state = getState()
    const { profileId } = state.auth
    dispatch(notificationActions.fetchLocalReminders())

    const {
      location: { search },
    } = state.router
    const currentQuery = parse(search)
    const profile = state.entities.profiles[String(profileId)]
    const isUnsubscribed = profile?.is_unsubscribed === null || profile?.is_unsubscribed === true
    if (isUnsubscribed) {
      setTimeout(() => {
        const newSearch = stringify({
          ...currentQuery,
          modal: 'newsletter',
        })
        const {
          router: { location },
        } = getState()
        if (!location.search.includes('modal')) {
          dispatch(routerActions.push(`?${newSearch}`))
        }
      }, 5000)
    }
  },

  login: (data: Record<string, unknown>): APIAction => ({
    CALL_API: {
      type: 'AUTH_LOGIN',
      method: 'POST',
      endpoint: '/api/auth/login/',
      data,
    },
  }),

  fbLogin: (data: Record<string, unknown>): APIAction => ({
    CALL_API: {
      type: 'AUTH_LOGIN',
      method: 'POST',
      endpoint: '/api/auth/facebook/',
      data,
    },
  }),

  logout: () => async (dispatch: Dispatch) => {
    localStorage.removeItem('isImpersonating')
    Cookie.remove('theme')
    await dispatch(actions.removeAuthToken())
    if (isBrowser) {
      document.location.href = '/'
    }
    return Promise.resolve()
  },

  resetPasswordConfirm: (data: Record<string, unknown>) => async (dispatch: Dispatch) => {
    const response = (await dispatch(actions.resetPasswordConfirmRequest(data))) as { data: { error?: string } }
    if (!response.data.error) {
      return dispatch(routerActions.push('/?modal=login'))
    }
    return Promise.resolve(response)
  },

  resetPasswordConfirmRequest: (data: Record<string, unknown>): APIAction => ({
    CALL_API: {
      type: 'AUTH_RESET_PASSWORD_CONFIRM',
      method: 'POST',
      endpoint: '/api/auth/password/reset/confirm/',
      data,
    },
  }),

  resetPassword: (data: Record<string, unknown>): APIAction => ({
    CALL_API: {
      type: 'AUTH_RESET_PASSWORD',
      method: 'POST',
      endpoint: '/api/auth/password/reset/',
      data,
    },
  }),

  showSignupModal:
    (action = '') =>
    (dispatch: Dispatch, getState: GetState) => {
      const state = getState()
      const { location } = state.router
      const q = parse(location.search) as { modal: string; redirect: string; action: string }
      q.modal = 'signup'
      if (action) {
        q.action = action
      }
      return dispatch(
        routerActions.push({
          pathname: location.pathname,
          search: stringify(q),
        }),
      )
    },
}
